import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import { Card, RangePicker, Select } from '_atoms';
import { FooterButton } from '_molecules';
import { useDebounceEffect } from 'helpers/useDebounceEffect';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { analyticsFilter } from 'actions/analyticActions';
import { ROUTES } from 'router/constants';
import { ReactComponent as ErrorIcon } from 'dist/icons/error.svg';
import type { AnalyticsAppliedFilters } from '../types';
import {
  companiesOptions,
  serviceOptions,
  jobsOptions,
  candidatesOptions,
  teamLeadsOptions,
  recruitersOptions,
  recruiterLeadsOptions,
  openingsOptions,
  officesOptions,
  PLACEHOLDERS,
  type AnalyticsFilterSelectConfig,
} from './constants';
import { RULES } from './utils';
import './analytics-filter.scss';

export interface AnalyticsFilterProps {
  form: FormInstance;
  initialFilterOptions: AnalyticsAppliedFilters & {
    company?: unknown;
    service?: unknown;
  };
  onApply: (values: AnalyticsAppliedFilters) => void;
  onReset: () => void;
}

type FilterHierarchyKey = keyof typeof filtersHierarchyObj;

const filtersHierarchyObj: Record<string, string[]> = {
  period: [],
  active: [],
  expected: [],
  created: [],
  companies: ['services', 'team_leads', 'jobs', 'offices', 'recruiters', 'openings_statuses', 'candidates'],
  services: ['team_leads', 'jobs', 'offices', 'recruiters', 'openings_statuses', 'candidates'],
  team_leads: ['jobs', 'offices', 'recruiters', 'openings_statuses', 'candidates'],
  jobs: ['offices', 'recruiters', 'openings_statuses', 'candidates'],
  offices: ['recruiters', 'openings_statuses', 'candidates'],
  recruiters: ['openings_statuses', 'candidates'],
  recruiter_leads: [],
  openings_statuses: ['candidates'],
  candidates: [],
};

export const AnalyticsFilter = ({ form, initialFilterOptions, onApply, onReset }: AnalyticsFilterProps) => {
  const { pathname: tab } = useLocation();

  const [company, setCompany] = useState<AnalyticsFilterSelectConfig>({
    ...companiesOptions,
    options: (initialFilterOptions.company as unknown[]) || [],
    selected: initialFilterOptions.company,
  });
  const [service, setService] = useState<AnalyticsFilterSelectConfig>({
    ...serviceOptions,
    options: (initialFilterOptions.service as unknown[]) || [],
    selected: initialFilterOptions.service,
  });
  const [job, setJob] = useState(jobsOptions);
  const [candidate, setCandidate] = useState(candidatesOptions);
  const [teamLead, setTeamLead] = useState(teamLeadsOptions);
  const [recruiter, setRecruiter] = useState(recruitersOptions);
  const [recruiterLead, setRecruiterLead] = useState(recruiterLeadsOptions);
  const [openingStatuses, setOpeningStatuses] = useState(openingsOptions);
  const [offices, setOffices] = useState(officesOptions);
  const [loadingField, setLoadingField] = useState('');
  const [periodsError, setPeriodsError] = useState(false);

  const selectorsOptions = React.useMemo(() => {
    if (tab === ROUTES.ANALYTICS_RECRUITERS) return [company, service, job, recruiter, candidate];
    if (tab === ROUTES.ANALYTICS_TEAM_LEADS) return [company, service, teamLead, job];
    if (tab === ROUTES.ANALYTICS_CANDIDATE) return [service, job, recruiter, candidate];
    if (tab === ROUTES.ANALYTICS_REPORTING) return [company, service, job, recruiter];
    if (tab === ROUTES.ANALYTICS_OPENINGS)
      return [
        company,
        service,
        job,
        offices,
        openingStatuses,
        recruiterLead,
        { ...teamLead, label: 'Opening Team Lead' },
      ];
    if (tab === ROUTES.ANALYTICS_SSC) return [];
    return [company, service, job, candidate];
  }, [tab, company, service, job, candidate, teamLead, recruiter, openingStatuses, offices, recruiterLead]);

  const getOptions = (url: string, search = '') => {
    setLoadingField(url);
    const filters = form.getFieldsValue();
    const initFilters = (({
      search: _s,
      ...rest
    }: AnalyticsAppliedFilters & { company?: unknown; service?: unknown }) => rest)(initialFilterOptions);
    const params = [...Object.entries(initFilters), ...Object.entries(filters)].reduce(
      (s, [key, value]) => ({ ...s, [key]: value }),
      {} as Record<string, unknown>
    );

    analyticsFilter(
      url.replace('_', '-'),
      params as unknown as Parameters<typeof analyticsFilter>[1],
      search,
      url === openingStatuses.select ? true : undefined
    )
      .then(data => {
        if (url === companiesOptions.select) setCompany(c => ({ ...c, options: data as unknown[] }));
        else if (url === serviceOptions.select) setService(s => ({ ...s, options: data as unknown[] }));
        else if (url === jobsOptions.select) setJob(j => ({ ...j, options: data as unknown[] }));
        else if (url === candidatesOptions.select) setCandidate(c => ({ ...c, options: data as unknown[] }));
        else if (url === recruitersOptions.select) setRecruiter(r => ({ ...r, options: data as unknown[] }));
        else if (url === recruiterLeadsOptions.select) setRecruiterLead(r => ({ ...r, options: data as unknown[] }));
        else if (url === teamLeadsOptions.select) setTeamLead(tl => ({ ...tl, options: data as unknown[] }));
        else if (url === openingStatuses.select) {
          setOpeningStatuses(os => ({
            ...os,
            options: (data as unknown as string[]).map(d => ({ value: d, label: d, id: d })),
          }));
        } else if (url === officesOptions.select) {
          setOffices(o => ({ ...o, options: data as unknown[] }));
        }
      })
      .finally(() => setLoadingField(''));
  };

  const onGetOptions = useDebounceEffect(getOptions, 300);

  const onBeforeApply = (values: AnalyticsAppliedFilters) => {
    if (
      tab === ROUTES.ANALYTICS_OPENINGS &&
      !values.active?.length &&
      !values.expected?.length &&
      !values.created?.length
    ) {
      setPeriodsError(true);
    } else {
      setPeriodsError(false);
      onApply(values);
    }
  };

  const onChangeFormValue = (fields: Record<string, unknown>) => {
    const fieldsToReset: Record<string, unknown> = {};
    const key = Object.keys(fields)[0] as FilterHierarchyKey;
    (filtersHierarchyObj[key] || []).forEach(field => {
      fieldsToReset[field] = initialFilterOptions[field as keyof AnalyticsAppliedFilters];
    });
    form.setFieldsValue(fieldsToReset);
  };

  return (
    <Card className={cn('analytics-filter', tab.replace('/analytics/', ''))}>
      <Form
        name="analytics-filters"
        form={form}
        initialValues={initialFilterOptions}
        onValuesChange={onChangeFormValue}
        onFinish={onBeforeApply}
        validateTrigger="onSubmit"
      >
        {periodsError && (
          <div className="no-periods-error">
            <ErrorIcon />
            Please select at least one period filter
          </div>
        )}
        <Row className="select-block" gutter={[32, 16]}>
          {tab === ROUTES.ANALYTICS_OPENINGS ? (
            <>
              <Col span={8}>
                <RangePicker label="Active in period" itemProps={{ name: 'active', rules: RULES.ACTIVE }} />
              </Col>
              <Col span={8}>
                <RangePicker label="Expected date period" itemProps={{ name: 'expected', rules: RULES.EXPECTED }} />
              </Col>
              <Col span={8}>
                <RangePicker label="Creation date period" itemProps={{ name: 'created', rules: RULES.CREATED }} />
              </Col>
            </>
          ) : (
            <Col span={8}>
              <RangePicker
                label="Period"
                itemProps={{ name: 'period' }}
                {...({ allowClear: false } as Record<string, unknown>)}
              />
            </Col>
          )}
          {selectorsOptions.map(option => (
            <Col key={option.label} span={8}>
              <Select
                labelInValue
                placeholder={PLACEHOLDERS[option.select]}
                label={option.label}
                filterOption={false}
                options={loadingField === option.select ? [] : (option.options as [])}
                onSearch={search => onGetOptions(option.select, search)}
                onDropdownVisibleChange={open => open && getOptions(option.select)}
                disabled={option.label === 'Companies' && !!initialFilterOptions.companies.length}
                empty={loadingField === option.select ? <div>Loading...</div> : null}
                mode={SELECT_TYPES.MULTIPLE}
                itemProps={{ name: option.select }}
              />
            </Col>
          ))}
        </Row>
        <FooterButton onReset={onReset} />
      </Form>
    </Card>
  );
};
